import React from "react";
import { createPortal } from "react-dom";
import InfoTip from "../../../../utils/InfoTip";
import { SORT_ROWS } from "../../../../utils/candidatesV2Constants";
import { RESTRICTED_TIP } from "../constants";
import { RefreshIcon } from "../icons";
import SortDropdown, { sortOptionValue } from "./SortDropdown";
import ExportDropdown from "./ExportDropdown";

// Match count, sort indicator and active filter chips. The Sort / Refresh / Export buttons
// are portalled into `actionsSlot` (the right side of the tabs bar) while this tab is shown.
// `cv` is the useCandidatesV2() result.
const CandidatesHeader = ({ cv, isRestricted, actionsSlot }) => {
  const {
    searchTerm, pagination, isDisabled, exportLoading,
    sortField, sortDirection, sortOptions, handleSortChange,
    activeFilterChips, handleClearAllFilters, removeChip,
    showExportDropdown, setShowExportDropdown, exportDropdownRef, exportDropdownPortalRef, handleExport,
    triggerFetch,
  } = cv;

  const isCustomSort = sortField !== "created_at" || sortDirection !== "desc";
  const currentSortLabel = sortOptions.length > 0
    ? sortOptions.find((opt) => sortOptionValue(opt) === sortField)?.label
    : SORT_ROWS.find((r) => r.field === sortField)?.label;

  const showMatchCount = activeFilterChips.length > 0 || !!searchTerm || isCustomSort;
  const showSortLabel = isCustomSort && !!currentSortLabel;

  const actions = (
    <div className="candidates-header-actions candidate-wise-actions">
      <SortDropdown
        sortField={sortField}
        sortDirection={sortDirection}
        sortOptions={sortOptions}
        onApply={handleSortChange}
        disabled={isDisabled}
      />
      <button type="button" className="tabs-refresh-btn" onClick={() => triggerFetch()} disabled={isDisabled} title="Refresh" aria-label="Refresh">
        <RefreshIcon size={15} />
      </button>
      <ExportDropdown
        open={showExportDropdown}
        setOpen={setShowExportDropdown}
        wrapRef={exportDropdownRef}
        portalRef={exportDropdownPortalRef}
        loading={exportLoading}
        disabled={isDisabled}
        onExport={handleExport}
      />
    </div>
  );
  const portalledActions = actionsSlot ? createPortal(actions, actionsSlot) : null;

  // Nothing left to show in the header itself — just the buttons up in the tabs bar.
  if (!showMatchCount && !isRestricted && !showSortLabel) return portalledActions;

  return (
    <div className="candidates-v2-header">
      {portalledActions}
      <div className="candidates-header-top-row">
        <div className="candidates-header-left">
          {showMatchCount && (
            <span className="candidates-match-count">
              <strong>{pagination.total || 0}</strong> candidates match your filters
            </span>
          )}
          {isRestricted && (
            <span className="assigned-only-note-v2">
              Showing access for candidates assigned to you
              <InfoTip text={RESTRICTED_TIP} />
            </span>
          )}
          {showSortLabel && (
            <span className="sort-indicator-v2">
              Sorted by: <strong>{currentSortLabel}</strong>
              <span className="sort-dir-indicator">{sortDirection === "desc" ? "↓" : "↑"}</span>
            </span>
          )}
        </div>
      </div>

      {/* Active filter chips – full width below header row */}
      {activeFilterChips.length > 0 && (
        <div className="active-filters-bar">
          {activeFilterChips.map((chip) => (
            <div key={chip.key} className="filter-chip">
              <span className="filter-chip-cat">{chip.category}:</span>
              <span>{chip.label}</span>
              <button className="chip-remove-btn" onClick={() => activeFilterChips.length === 1 ? handleClearAllFilters() : removeChip(chip.key)} title="Remove filter">×</button>
            </div>
          ))}
          <button className="clear-filters-link" onClick={handleClearAllFilters}>Clear all</button>
        </div>
      )}
    </div>
  );
};

export default CandidatesHeader;
